import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { resetPassword, verifyResetToken } from '@/apis';
import unionLogo from '@/assets/images/union-logo.png';
import * as AuthComponent from '@/components/Auth';
import BackButton from '@/components/BackButton';
import Logo from '@/components/Logo';
import OutlinedButton from '@/components/OutlinedButton';
import { useLoading } from '@/contexts/LoadingContext';
import { ButtonHtmlType, Path } from '@/enums';
import { useBreakpoint } from '@/hooks/useMediaQuery';

import * as Style from './ResetPassword.style';

interface ResetPasswordFormValues {
  password: string;
}

const ResetPassword = () => {
  const [form] = Form.useForm<ResetPasswordFormValues>();
  const [success, setSuccess] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { withLoading } = useLoading();
  const { isMobileOrTablet } = useBreakpoint();

  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setTokenExpired(true);
        setErrorMessage('缺少重設密碼連結');
        return;
      }

      try {
        const response = await verifyResetToken(token);
        if (!response.data.valid) {
          setTokenExpired(true);
          setErrorMessage(
            response.data.message || '此重設密碼連結已過期或無效',
          );
        }
      } catch (error) {
        console.error('Error verifying reset token:', error);
        setTokenExpired(true);
        setErrorMessage('無法驗證重設密碼連結，請重新申請');
      }
    };

    checkToken();
  }, [token]);

  const onFinish = async (values: ResetPasswordFormValues) => {
    if (!token) return;
    await withLoading(async () => {
      await resetPassword(token, values.password);
      setSuccess(true);
    });
  };

  if (tokenExpired) {
    return (
      <Style.SuccessContainer vertical>
        <Logo
          src={unionLogo}
          alt="Union Logo"
          height="80px"
          style={{ marginBottom: '24px' }}
        />

        <Style.Title style={{ color: '#ff4d4f', marginBottom: '16px' }}>
          連結已過期
        </Style.Title>

        <Style.SuccessMessage style={{ marginBottom: '24px' }}>
          {errorMessage}
        </Style.SuccessMessage>

        <OutlinedButton
          height="4rem"
          width="12rem"
          onClick={() => navigate(Path.ForgotPassword)}
          style={{ margin: '0 auto' }}
        >
          前往忘記密碼頁面
        </OutlinedButton>

        {isMobileOrTablet && <BackButton to={Path.Login} />}
      </Style.SuccessContainer>
    );
  }

  if (success) {
    return (
      <Style.SuccessContainer vertical>
        <Logo
          src={unionLogo}
          alt="Union Logo"
          height="80px"
          style={{ marginBottom: '24px' }}
        />

        <Style.SuccessMessage>密碼重設成功！</Style.SuccessMessage>

        <OutlinedButton
          onClick={() => navigate(Path.Login)}
          height="4rem"
          width="8rem"
        >
          前往登入
        </OutlinedButton>

        {isMobileOrTablet && <BackButton to={Path.Login} />}
      </Style.SuccessContainer>
    );
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      requiredMark={false}
      style={{ width: '100%' }}
    >
      <Style.FormContainer vertical>
        <Logo
          src={unionLogo}
          alt="Union Logo"
          height="80px"
          style={{ marginBottom: '24px' }}
        />

        <Style.Title>重設密碼</Style.Title>

        <Form.Item
          name="password"
          rules={[
            {
              required: true,
              message: '請輸入新密碼',
            },
            {
              min: 6,
              message: '密碼至少需要6個字符',
            },
          ]}
        >
          <AuthComponent.InputPassword
            visibilityToggle={false}
            placeholder="請輸入新密碼"
            width="100%"
            height="3.5rem"
          />
        </Form.Item>

        <Style.ButtonGroup>
          <OutlinedButton
            htmlType={ButtonHtmlType.Submit}
            height="4rem"
            width="8rem"
          >
            重設密碼
          </OutlinedButton>
        </Style.ButtonGroup>

        {isMobileOrTablet && <BackButton to={Path.Login} />}
      </Style.FormContainer>
    </Form>
  );
};

export default ResetPassword;
